import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { api, routeApi } from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useNavigate } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";
import { useCallback, useState } from "react";
import { downloadCSV } from "@shared/utils/utils";
import { toast } from "@shared/ui/use-toast";
import { id } from "@rolldown/pluginutils";

export const useGetAllRoutes = (
  page: any,
  search_data: string,
  readType: any,
  area: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.list(
      page,
      search_data,
      readType,
      area
    ),
    () => api.routes.get.list(page, utilityId, search_data, readType, area),
    {
      enabled: !!page && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useRoutesExport = (
  utilityId: string,
  search_data?: string,
  readType?: string,
  area?: string
) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const blob = await api.routes.get.exportAllRoutes(
        undefined, // No page for export to get all data
        utilityId,
        search_data,
        readType,
        area
      );

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export routes: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [utilityId, search_data, readType, area, isExporting]);

  return { exportCSV, isExporting };
};

export const useGetCategory = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.category(),
    () => api.routes.get.category(utilityId),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetSubCategory = (code_list: any[]) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.subCategory(code_list),
    () => api.routes.get.subCategory(utilityId, code_list),
    {
      enabled: !!code_list?.length && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetMeterSummaryDashboardByPremises = (
  premiseCode?: any[],
  category?: any[],
  subCategory?: any[],
  utilityService?: any[],
  meterType?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.meterSummaryDashboardByPremises(
      premiseCode,
      category,
      subCategory,
      utilityService,
      meterType
    ),
    () =>
      api.routes.create.DashboardByPremises(
        utilityId,
        premiseCode,
        category,
        subCategory,
        utilityService,
        meterType
      ),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetPremises = (premiseCode?: any[]) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.premises(premiseCode),
    () => api.routes.get.premises(utilityId, premiseCode),
    {
      enabled: !!premiseCode?.length && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetMeterSummaryDashboard = (routeId: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterSummaryDashboard(
      routeId
    ),
    () => api.routes.create.Dashboard(routeId, utilityId),
    {
      enabled: !!routeId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetRouteData = (routeId: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.routeData(routeId),
    () => api.routes.get.routeData(routeId, utilityId),
    {
      enabled: !!routeId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetMetersCount = (
  routeId: any,
  page: any,
  meter_type: any,
  filter_status: any,
  reading_status: any,
  search_data?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.metersCount(
      routeId,
      page,
      meter_type,
      filter_status,
      reading_status,
      search_data
    ),
    () =>
      api.routes.get.metersCount(
        routeId,
        page,
        utilityId,
        meter_type,
        filter_status,
        reading_status,
        search_data
      ),
    {
      enabled: !!routeId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetPerformanceDashboard = (routeId: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.performanceDashboard(routeId),
    () => api.routes.get.performanceDashboard(routeId, utilityId),
    {
      enabled: !!routeId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateRoute = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) => api.routes.create.createRoute(payload),
    {
      onSuccess: () => {
        toast({
          title: "Route created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "route", "list"],
        });
        navigate("/mx/routeManagement/?readType=all&area=all", {
          replace: true,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.mx.routesManagement.create(),
        });
      },
    }
  );
};

export const useUpdateRoute = (routeId: any) => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) => api.routes.create.updateRoute(routeId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Route updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "route", "routeData", routeId],
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.mx.routesManagement.create(),
        });
        navigate("/mx/routeManagement/?readType=all&area=all", {
          replace: true,
        });
      },

      onError: (error: any) => {
        toast({
          title: `Failed to update route: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useRouteAuditTrail = (routeId: any, actionType: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.auditTrail(routeId, actionType),
    () => api.routes.get.auditTrail(routeId, utilityId, actionType),
    {
      enabled: !!routeId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useMeterCycle = (
  routeId: any,
  searchData?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.meterCycle({
      routeId,
      searchData
    }),
    () => routeApi.getMeterCycle(routeId, searchData),
  );
};
export const useRoutePrimise = (
  code_name: string | string[],
  searchData?: string,
  page?: number,
  limit?: number,
  area?: string,      // Add this
  subArea?: string    // Add this
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.routePremise({
      code_name,
      searchData,
      page,
      limit,
      area,      // Add this
      subArea    // Add this
    }),
    () => routeApi.getRoutePremise(code_name, searchData, page, limit, area, subArea), // Pass them here
    {
      enabled: Array.isArray(code_name) ? code_name.length > 0 : !!code_name
    }
  );
};

export const useUpdateReadCycle = () => {
  return useSmartMutation(
    (payload: any) => routeApi.updateReadCycle(payload),
    {
      onSuccess: () => {
        toast({
          title: "Read cycle updated successfully!",
        });

    
      },
      onError: (error: any) => {
        toast({
          title: "Failed to update read cycle",
          description:
            error?.response?.data?.message || "An unexpected error occurred.",
          variant: "destructive",
        });
      },
    }
  );
};

export const useAddRoute = () => {
  return useSmartMutation(
    (payload: any) => routeApi.addRoute(payload),
  );
};

export const useAddPremises = (routeId: any,newRouteId?:any) => {
  return useSmartMutation(
    (payload: any) => routeApi.addPremises(payload, routeId,newRouteId),
    {
      onSuccess: () => {
        toast({
          title: "Premise added successfully!",
          description: "The new premise has been added to the selected route.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to add premise",
          description:
            error?.response?.data?.message ||
            "Something went wrong while adding the premise.",
        });
      },
    }
  );
};

export const useAddPremisesModal = (routeId: any) => {
  return useSmartMutation(
    (payload: any) => routeApi.addPremisesModal(payload, routeId),
    {
      onSuccess: () => {
        toast({
          title: "Premise added successfully!",
          description: "The new premise has been added to the selected route.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to add premise",
          description:
            error?.response?.data?.message ||
            "Something went wrong while adding the premise.",
        });
      },
    }
  );
};

export const useRouteSummary = (

) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.routeSummary(

    ),
    () => routeApi.getRouteSummary(),
  );
};

export const usePremise = (page?: number,limit?:number, area?: string, sub_area?: string, search_data?: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.premise(page, area, sub_area, search_data),
    () => routeApi.getPremise(page,limit, area, sub_area, search_data)
  );
};

export const usePremiseModal = (
  page?: number,
  area?: string,
  sub_area?: string,
  search_data?: string,
  exclude_code_name?: any
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.premise(
      page,
      area,
      sub_area,
      search_data,
      exclude_code_name
    ),
    () =>
      routeApi.getPremiseModal(
        page,
        area,
        sub_area,
        search_data,
        exclude_code_name
      ),
    {
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );
};


export const useMeters = (
  premise: string | string[],
  page?: number,
  limit?: number
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.meters({ premise, page, limit }),
    () => routeApi.getMeters({ premise, page, limit }),
  );
};



  export const useRouteDashboard = (
routeId
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.routeDashboard(
routeId
    ),
    () => routeApi.getRouteDashboard(routeId),
    
  );
};

  export const useMeterSummary = (
premise
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.meterSummary(
premise
    ),
    () => routeApi.getMeterSummary(premise),
    
  );
};


export const useGetPerformanceDetails = (
  routeId: any,
  id?: number
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.performanceDetails(
      routeId,
      id
    ),
    () =>
      routeApi.getPerformanceDetails(
        routeId,
        utilityId,
        id
      ),
    {
      enabled: !!routeId && !!utilityId,
    }
  );
};


export const useReadingStatus = () => {
  return useSmartQuery(QueryKeyFactory.module.mx.routesManagement.readingStatus(), () =>
    routeApi.getReadingStatus()
  );
};

export const useMeterType = () => {
  return useSmartQuery(QueryKeyFactory.module.mx.routesManagement.meterType(), () =>
    routeApi.getMeterType()
  );
};

export const useMeterStatus = () => {
  return useSmartQuery(QueryKeyFactory.module.mx.routesManagement.meterStatus(), () =>
    routeApi.getMeterStatus()
  );
};
